/**
 * FormObjectPicker -- a Reference field that picks RECORDS, chosen through the classification's own
 * list screen.
 *
 * ED, 2026-10-28: "Use the same picker for the contract's Items." The same picker is the Purchase
 * Orders chooser on a contract: the list page in picker mode -- Filter By on the left, the
 * visibility tabs the persona is allowed, both search boxes, the grid's own paging -- contained in
 * a dialog with Cancel and Select bottom right. A form field whose type resolves an object
 * classification (the line form's Item, the supplier form's Item) gets that screen, so a tenant
 * with thousands of items narrows the list the way they narrow it anywhere else, instead of
 * scrolling a dropdown over the first two hundred.
 *
 * WHAT IT STORES: the logical object's USID -- what the resolver stores and the list page hands
 * back -- so a reference tracks the record through its revisions, as it always did.
 *
 * WHAT IT SHOWS: the chosen record's name. The field's resolved options carry a Display for a value
 * within the option limit; a record picked from page 40 is not among them, so the name is kept from
 * the pick itself. A value with neither (held from before, past the limit) shows its number.
 */
import React, {Suspense, useMemo, useState} from "react";
import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField,
        Typography} from "@mui/material";
import {ListAlt} from "@mui/icons-material";

import {OBJECT_CLASSIFICATIONS} from "../../util/objectClassifications";
import type {PickedRecord} from "../../pages/ObjectList";
import {Grey7} from "../../theme/styles";

// The list page draws this control's own ancestors (its New dialog renders forms), so it is loaded
// when the chooser opens rather than imported at module load, which would close a cycle.
const ObjectList = React.lazy(() => import("../../pages/ObjectList"));

export function picksFromListScreen(classificationCode: string | null | undefined): boolean {
    return !!classificationCode && OBJECT_CLASSIFICATIONS.some(one => one.code === classificationCode);
}

export function FormObjectPicker({label, classificationCode, options, many, value, onChange, disabled}: {
    label: string;
    classificationCode: string;
    options: {Value: string; Display: string | null}[];
    many: boolean;
    value: any;
    onChange: (next: any) => void;
    disabled?: boolean;
}) {
    const classification = OBJECT_CLASSIFICATIONS.find(one => one.code === classificationCode)!;
    const [open, setOpen] = useState(false);
    const [ticked, setTicked] = useState<PickedRecord[]>([]);
    // Names learned from picks, for values the option list does not reach.
    const [named, setNamed] = useState<Record<string, string>>({});
    const [problem, setProblem] = useState<string | null>(null);

    const chosen: string[] = many
        ? (Array.isArray(value) ? value.map(String) : [])
        : (value === null || value === undefined || value === "" ? [] : [String(value)]);

    const displayOf = useMemo(() => {
        const byValue: Record<string, string | null> = {};
        options.forEach(one => { byValue[one.Value] = one.Display; });
        return (usid: string) => byValue[usid] ?? named[usid] ?? usid;
    }, [options, named]);

    const select = () => {
        const picked = ticked.filter(one => one.ObjectUSID);
        // SAID, NOT SWALLOWED. A ticked row with no object behind it cannot happen now that a tick
        // waits for the row to be drawn, but if it ever does the reader is told rather than
        // handed a closed dialog and an unchanged field.
        if (ticked.length && picked.length === 0) {
            setProblem("The ticked record has not finished loading. Tick it again.");
            return;
        }
        setProblem(null);
        setNamed(previous => {
            const next = {...previous};
            picked.forEach(one => {
                next[String(one.ObjectUSID)] = [one.ObjectID, one.Name].filter(Boolean).join(" — ") || String(one.ObjectUSID);
            });
            return next;
        });
        const usids = picked.map(one => String(one.ObjectUSID));
        onChange(many ? usids : (usids[0] ?? null));
        setOpen(false);
    };

    const summary = chosen.map(displayOf).join(", ");

    return (
        <>
            <Box sx={{display: "flex", alignItems: "center", gap: "6px"}}>
                <TextField size="small" fullWidth value={summary} placeholder="Select"
                           onClick={() => !disabled && setOpen(true)}
                           disabled={disabled}
                           slotProps={{input: {readOnly: true, style: {cursor: disabled ? "default" : "pointer"}},
                                       htmlInput: {style: {cursor: disabled ? "default" : "pointer"}}}}/>
                <IconButton size="small" disabled={disabled} onClick={() => setOpen(true)}
                            aria-label={`Choose ${label}`}>
                    <ListAlt sx={{fontSize: 22}}/>
                </IconButton>
            </Box>
            {open && (
                <Dialog open onClose={() => setOpen(false)} fullWidth maxWidth="xl"
                        slotProps={{paper: {sx: {height: "90vh", maxHeight: "90vh", display: "flex", flexDirection: "column"}}}}>
                    <DialogTitle sx={{fontSize: 16, paddingBottom: "6px", flex: "0 0 auto"}}>{label}</DialogTitle>
                    {problem && (
                        <Typography sx={{fontSize: 13, color: "#B3261E", padding: "0 24px 6px", flex: "0 0 auto"}}>{problem}</Typography>
                    )}
                    <DialogContent sx={{padding: 0, flex: "1 1 auto", minHeight: 0, overflow: "hidden",
                                        display: "flex", flexDirection: "column"}}>
                        <Suspense fallback={<Typography sx={{fontSize: 13, color: Grey7, padding: "24px"}}>Loading…</Typography>}>
                            <ObjectList classification={classification}
                                        picker={{multiple: many, onSelectionChange: setTicked}}/>
                        </Suspense>
                    </DialogContent>
                    <DialogActions sx={{padding: "12px 24px", borderTop: "1px solid #E4E7EC", flex: "0 0 auto"}}>
                        <Button onClick={() => setOpen(false)} sx={{textTransform: "none"}}>Cancel</Button>
                        <Button variant="contained" disableElevation onClick={select}
                                disabled={ticked.length === 0} sx={{textTransform: "none"}}>
                            Select{many && ticked.length > 0 ? ` (${ticked.length})` : ""}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
}
